import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';

interface DetailPhotoLightboxPhoto {
  id: number;
  displayUrl: string;
  originalName: string;
}

interface DetailPhotoLightboxLabels {
  close: string;
  previous: string;
  next: string;
  counter: (current: number, total: number) => string;
}

interface DetailPhotoLightboxProps {
  photos: DetailPhotoLightboxPhoto[];
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  labels: DetailPhotoLightboxLabels;
}

/**
 * Full-screen photo viewer for the media unit detail gallery. Modeled on
 * `client-offer-review-lightbox-modal.tsx` (offers feature) but adapted for a
 * real bounded photo array (no wrap-around) and adds keyboard support the
 * reference component does not have: Escape to close, ArrowLeft/ArrowRight to
 * navigate. Focus moves to the close button on open and returns to whatever
 * was focused beforehand (the gallery trigger) on close.
 */
export function DetailPhotoLightbox({
  photos,
  activeIndex,
  onClose,
  onNavigate,
  labels,
}: DetailPhotoLightboxProps) {
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const previouslyFocused = React.useRef<Element | null>(null);
  const activePhoto = photos[activeIndex];
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < photos.length - 1;

  React.useEffect(() => {
    previouslyFocused.current = document.activeElement;
    closeButtonRef.current?.focus();
    return () => {
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, []);

  React.useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' && activeIndex > 0) {
        onNavigate(activeIndex - 1);
      } else if (event.key === 'ArrowRight' && activeIndex < photos.length - 1) {
        onNavigate(activeIndex + 1);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, photos.length, onClose, onNavigate]);

  if (activePhoto == null) return null;

  const content = (
    // DS-BYPASS: always near-black overlay chrome, independent of the app's
    // light/dark theme (packages/ui/src/styles/globals.css has no always-dark
    // surface token). Same reasoning as client-offer-review-lightbox-modal.tsx.
    <div
      onClick={onClose}
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-3.5 bg-zinc-950/90"
    >
      <Button
        ref={closeButtonRef}
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onClose}
        aria-label={labels.close}
        data-testid="photo-lightbox-close"
        className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </Button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex items-center justify-center"
        style={{ width: 'min(92vw, 1400px)', height: 'min(78vh, 900px)' }}
      >
        <img
          src={activePhoto.displayUrl}
          alt={activePhoto.originalName}
          className="h-full w-full rounded-xl object-contain"
          data-testid="photo-lightbox-image"
        />

        {hasPrev ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(activeIndex - 1);
            }}
            aria-label={labels.previous}
            data-testid="photo-lightbox-prev"
            className="absolute -left-2 top-1/2 h-11 w-11 -translate-x-full -translate-y-1/2 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2.2} />
          </Button>
        ) : null}

        {hasNext ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(activeIndex + 1);
            }}
            aria-label={labels.next}
            data-testid="photo-lightbox-next"
            className="absolute -right-2 top-1/2 h-11 w-11 translate-x-full -translate-y-1/2 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
          </Button>
        ) : null}
      </div>

      {photos.length > 1 ? (
        <span
          onClick={(e) => e.stopPropagation()}
          className="text-xs tabular-nums text-zinc-400"
          data-testid="photo-lightbox-counter"
        >
          {labels.counter(activeIndex + 1, photos.length)}
        </span>
      ) : null}
    </div>
  );

  return createPortal(content, document.body);
}
